import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Modal from '../components/Modal';

function MonitoringConfigs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "all";
  const [configs, setConfigs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedConfigs, setSelectedConfigs] = useState([]);
  const [maxDateCache, setMaxDateCache] = useState({});
  const [journeyStatusCache, setJourneyStatusCache] = useState({});
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyConfig, setHistoryConfig] = useState(null);
  const [formData, setFormData] = useState({
    sfmcAccountId: '',
    configName: '',
    objectType: 'automation',
    objectId: '',
    objectName: '',
    checkIntervalMinutes: 60,
    sendInstantAlerts: true,
    includeInDailyReport: true,
    thresholdMin: '',
    thresholdMax: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const [configsRes, accountsRes] = await Promise.all([
        axios.get('/api/monitoring-configs'),
        axios.get('/api/sfmc-accounts')
      ]);
      setConfigs(configsRes.data);
      setAccounts(accountsRes.data.filter(a => a.is_active));
      setSelectedConfigs([]);
      if (manual) {
        setSuccess('Data refreshed');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  // Fetch max date for a data extension
  const fetchMaxDate = async (config) => {
    if (config.object_type !== 'data_extension' && config.object_type !== 'dataextension') return;
    if (maxDateCache[config.object_id] !== undefined) return;
    
    try {
      const response = await axios.get(`/api/sfmc-objects/data-extensions/${config.object_id}/max-date?accountId=${config.sfmc_account_id}`);
      setMaxDateCache(prev => ({
        ...prev,
        [config.object_id]: response.data.maxDate || 'No data'
      }));
    } catch (err) {
      setMaxDateCache(prev => ({
        ...prev,
        [config.object_id]: 'Error'
      }));
    }
  };

  const fetchJourneyStatus = async (config) => {
    if (config.object_type !== "journey") return;
    if (journeyStatusCache[config.object_id] !== undefined) return;
    
    setJourneyStatusCache(prev => ({
      ...prev,
      [config.object_id]: "Loading..."
    }));
    
    try {
      const response = await axios.get("/api/sfmc-objects/journeys/" + config.object_id + "/status?accountId=" + config.sfmc_account_id);
      setJourneyStatusCache(prev => ({
        ...prev,
        [config.object_id]: response.data.status || "unknown"
      }));
    } catch (err) {
      setJourneyStatusCache(prev => ({
        ...prev,
        [config.object_id]: "Error"
      }));
    }
  };


  // Fetch max dates and journey statuses when configs change
  useEffect(() => {
    if (configs.length > 0) {
      const deConfigs = configs.filter(c => c.object_type === 'data_extension' || c.object_type === 'dataextension');
      deConfigs.forEach((config, index) => {
        if (maxDateCache[config.object_id] === undefined) {
          setTimeout(() => fetchMaxDate(config), index * 1000);
        }
      });
      
      const journeyConfigs = configs.filter(c => c.object_type === "journey");
      journeyConfigs.forEach((config, index) => {
        if (journeyStatusCache[config.object_id] === undefined) {
          setTimeout(() => fetchJourneyStatus(config), index * 1000);
        }
      });
    }
  }, [configs]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedConfigs(filteredConfigs.map(c => c.id));
    } else {
      setSelectedConfigs([]);
    }
  };

  const handleSelectConfig = (configId) => {
    setSelectedConfigs(prev => {
      if (prev.includes(configId)) {
        return prev.filter(id => id !== configId);
      } else {
        return [...prev, configId];
      }
    });
  };

  const handleBulkAction = async (action) => {
    if (selectedConfigs.length === 0) {
      setError('No configs selected');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const actionText = action === 'delete' ? 'delete' : action;
    if (!window.confirm(`Are you sure you want to ${actionText} ${selectedConfigs.length} config(s)?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await axios.post('/api/monitoring-configs/bulk-action', {
        action,
        configIds: selectedConfigs
      });
      await fetchData();
      setSuccess(`Successfully ${actionText}d ${selectedConfigs.length} config(s)`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${actionText} configs`);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        sfmcAccountId: config.sfmc_account_id,
        configName: config.config_name,
        objectType: config.object_type,
        objectId: config.object_id,
        objectName: config.object_name,
        checkIntervalMinutes: config.check_interval_minutes,
        sendInstantAlerts: config.send_instant_alerts,
        includeInDailyReport: config.include_in_daily_report,
        thresholdMin: config.threshold_min || '',
        thresholdMax: config.threshold_max || ''
      });
    } else {
      setEditingConfig(null);
      setFormData({
        sfmcAccountId: accounts[0]?.id || '',
        configName: '',
        objectType: 'automation',
        objectId: '',
        objectName: '',
        checkIntervalMinutes: 60,
        sendInstantAlerts: true,
        includeInDailyReport: true,
        thresholdMin: '',
        thresholdMax: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingConfig) {
        await axios.put(`/api/monitoring-configs/${editingConfig.id}`, formData);
        setSuccess('Configuration updated successfully');
      } else {
        await axios.post('/api/monitoring-configs', formData);
        setSuccess('Configuration created successfully');
      }
      await fetchData();
      handleCloseModal();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save config');
    }
  };

  const handleViewHistory = async (config) => {
    if (!['automation', 'automation_errors'].includes(config.object_type)) {
      return;
    }
    setHistoryConfig(config);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryData(null);

    try {
      const response = await axios.get(`/api/monitoring-configs/${config.id}/automation-history?days=7`);
      setHistoryData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load automation history');
      setTimeout(() => setError(''), 5000);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDelete = async (configId, configName) => {
    if (!window.confirm(`Are you sure you want to delete "${configName}"?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await axios.delete(`/api/monitoring-configs/${configId}`);
      await fetchData();
      setSuccess(`"${configName}" has been deleted successfully`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete config');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleTogglePause = async (configId, configName, currentEnabled) => {
    try {
      setError('');
      setSuccess('');
      await axios.patch('/api/monitoring-configs/' + configId, {
        enabled: !currentEnabled
      });
      await fetchData();
      setSuccess('"' + configName + '" has been ' + (currentEnabled ? 'paused' : 'resumed'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update config');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCheckNow = async (configId, configName, config) => {
    try {
      setError('');
      setSuccess('');
      setSuccess(`Checking "${configName}"...`);

      // Clear caches to force refetch after check
      if (config) {
        const isDE = config.object_type === 'data_extension' || config.object_type === 'dataextension';
        const isJourney = config.object_type === 'journey';

        if (isDE) {
          setMaxDateCache(prev => {
            const newCache = { ...prev };
            delete newCache[config.object_id];
            return newCache;
          });
        }
        if (isJourney) {
          setJourneyStatusCache(prev => {
            const newCache = { ...prev };
            delete newCache[config.object_id];
            return newCache;
          });
        }
      }

      // Clear the error message for this specific config before checking
      setConfigs(prevConfigs =>
        prevConfigs.map(c =>
          c.id === configId
            ? { ...c, last_check_message: null }
            : c
        )
      );

      await axios.post(`/api/monitoring-configs/${configId}/check`);

      // Poll for results every 1 second for up to 10 seconds
      let pollCount = 0;
      const maxPolls = 10;

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const response = await axios.get('/api/monitoring-configs');
          const updatedConfig = response.data.find(c => c.id === configId);

          if (updatedConfig && updatedConfig.last_check_at) {
            const lastCheck = new Date(updatedConfig.last_check_at);
            const now = new Date();

            // If check was done in last 5 seconds, we got the result
            if ((now - lastCheck) / 1000 < 5) {
              clearInterval(pollInterval);
              setConfigs(response.data);

              // Refetch max date or journey status after check completes
              if (config) {
                const isDE = config.object_type === 'data_extension' || config.object_type === 'dataextension';
                const isJourney = config.object_type === 'journey';

                if (isDE) {
                  fetchMaxDate(config);
                }
                if (isJourney) {
                  fetchJourneyStatus(config);
                }
              }

              setSuccess(`Check complete for "${configName}"`);
              setTimeout(() => setSuccess(''), 5000);
            }
          }

          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            await fetchData();

            // Refetch max date or journey status after timeout
            if (config) {
              const isDE = config.object_type === 'data_extension' || config.object_type === 'dataextension';
              const isJourney = config.object_type === 'journey';

              if (isDE) {
                fetchMaxDate(config);
              }
              if (isJourney) {
                fetchJourneyStatus(config);
              }
            }

            setSuccess(`Check triggered for "${configName}"`);
            setTimeout(() => setSuccess(''), 5000);
          }
        } catch (err) {
          // Continue polling even if one request fails
        }
      }, 1000);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trigger check');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.account_name || 'Unknown';
  };

  const formatLastCheck = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'healthy': return 'healthy';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'unknown';
    }
  };
// Get effective status for DE configs based on max date
  // Use database status - the worker already computed the correct status based on thresholds
  const getEffectiveStatus = (config) => {
    return config.last_check_status || "unknown";
  };

  // Get the appropriate last check value based on object type
  const getLastCheckDisplay = (config) => {
    const isDE = config.object_type === "data_extension" || config.object_type === "dataextension";
    const isJourney = config.object_type === "journey";
    
    if (isDE) {
      const maxDate = maxDateCache[config.object_id];
      if (maxDate && maxDate !== "No data" && maxDate !== "Error" && maxDate !== "Loading...") {
        return maxDate;
      }
    }
    
    // For journeys and other types, fall back to last_check_at
    return config.last_check_at ? formatLastCheck(config.last_check_at) : "Never";
  };


  // Filter configs based on status from URL
  const filteredConfigs = React.useMemo(() => {
    if (statusFilter === 'all') return configs;
    return configs.filter(config => getEffectiveStatus(config) === statusFilter);
  }, [configs, statusFilter]);

  const clearStatusFilter = () => {
    setSearchParams({});
  };

  if (loading) {
    return <div className="loading">Loading configs...</div>;
  }

  return (
    <div>
      <div className="content-header">
        <div>
          <h1 style={{ margin: '0 0 5px 0' }}>Monitoring Configs</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Configure what to monitor in your SFMC accounts
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleOpenModal()}
            disabled={accounts.length === 0}
          >
            + Add Config
          </button>
        </div>
      </div>

      <div className="content-body">
        {statusFilter !== 'all' && (
          <div style={{
            padding: '12px 16px',
            background: statusFilter === 'error' ? '#fee2e2' : '#fef3c7',
            border: '1px solid ' + (statusFilter === 'error' ? '#fecaca' : '#fcd34d'),
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: statusFilter === 'error' ? '#991b1b' : '#92400e', fontWeight: '500' }}>
              Showing {filteredConfigs.length} config{filteredConfigs.length !== 1 ? 's' : ''} with status: <strong style={{ textTransform: 'capitalize' }}>{statusFilter}</strong>
            </span>
            <button
              onClick={clearStatusFilter}
              style={{
                padding: '6px 12px',
                background: 'white',
                border: '1px solid ' + (statusFilter === 'error' ? '#fecaca' : '#fcd34d'),
                borderRadius: '6px',
                color: statusFilter === 'error' ? '#dc2626' : '#d97706',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Clear Filter
            </button>
          </div>
        )}
        {error && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            padding: '12px 20px',
            background: '#fecaca',
            color: '#991b1b',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: '250px',
            maxWidth: '400px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            padding: '12px 20px',
            background: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: '250px',
            maxWidth: '400px'
          }}>
            {success}
          </div>
        )}

        {accounts.length === 0 ? (
          <div className="empty-state">
            <h3>No SFMC Accounts</h3>
            <p>Add an SFMC account first before creating monitoring configs</p>
          </div>
        ) : configs.length === 0 ? (
          <div className="empty-state">
            <h3>No Monitoring Configs</h3>
            <p>Create your first monitoring config to start tracking SFMC objects</p>
            <button
              className="btn btn-primary"
              onClick={() => handleOpenModal()}
            >
              + Add Config
            </button>
          </div>
        ) : (
          <>
            {selectedConfigs.length > 0 && (
              <div style={{
                marginBottom: '15px',
                padding: '12px',
                background: '#eff6ff',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ color: '#1e40af', fontWeight: '500' }}>
                  {selectedConfigs.length} config(s) selected
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-success"
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                    onClick={() => handleBulkAction('enable')}
                  >
                    Enable Selected
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                    onClick={() => handleBulkAction('disable')}
                  >
                    Disable Selected
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                    onClick={() => handleBulkAction('delete')}
                  >
                    Delete Selected
                  </button>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-body">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedConfigs.length === filteredConfigs.length}
                          onChange={handleSelectAll}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th style={{ width: '80px' }}>Status</th>
                      <th style={{ width: '60px' }}>Active</th>
                      <th style={{ width: '180px' }}>Config Name</th>
                      <th style={{ width: '140px' }}>Account</th>
                      <th style={{ width: '110px' }}>Type</th>
                      <th style={{ width: '80px' }}>Interval</th>
                      <th style={{ width: '160px' }}>Last Check</th>
                      <th style={{ width: '200px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConfigs.map((config) => (
                      <tr key={config.id} style={{ opacity: config.enabled === false ? 0.5 : 1 }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedConfigs.includes(config.id)}
                            onChange={() => handleSelectConfig(config.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor:
                                  getEffectiveStatus(config) === 'healthy' ? '#16a34a' :
                                  getEffectiveStatus(config) === 'warning' ? '#ea580c' :
                                  getEffectiveStatus(config) === 'error' ? '#dc2626' :
                                  '#9ca3af',
                                flexShrink: 0
                              }}
                            />
                            <span className={`status-badge ${getStatusClass(getEffectiveStatus(config))}`}>
                              {getEffectiveStatus(config)}
                            </span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {config.enabled !== false ? (
                            <span style={{ color: '#16a34a', fontSize: '16px' }}>✓</span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '11px' }}>Paused</span>
                          )}
                        </td>
                        <td>
                          <strong style={{ 
                            display: "block",
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }} title={config.config_name}>{config.config_name}</strong>
                          
                        </td>
                        <td>{getAccountName(config.sfmc_account_id)}</td>
                        <td style={{ textTransform: 'capitalize' }}>{config.object_type}</td>
                        <td>{config.check_interval_minutes} min</td>
                        <td>{getLastCheckDisplay(config)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                            <button
                              className="btn btn-success"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => handleCheckNow(config.id, config.config_name, config)}
                            >
                              Check
                            </button>
                            <button
                              className={config.enabled !== false ? "btn btn-warning" : "btn btn-success"}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => handleTogglePause(config.id, config.config_name, config.enabled !== false)}
                            >
                              {config.enabled !== false ? 'Pause' : 'Resume'}
                            </button>
                            {['automation', 'automation_errors'].includes(config.object_type) && (
                              <button
                                className="btn btn-info"
                                style={{ padding: '4px 8px', fontSize: '11px', background: '#0891b2', color: 'white', border: 'none' }}
                                onClick={() => handleViewHistory(config)}
                              >
                                Graph
                              </button>
                            )}
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => handleOpenModal(config)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => handleDelete(config.id, config.config_name)}
                            >
                              Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingConfig ? 'Edit Monitoring Config' : 'Add Monitoring Config'}
      >
        {error && (
          <div style={{
            padding: '12px',
            background: '#fecaca',
            color: '#991b1b',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} id="config-form">
          <div className="form-group">
            <label>SFMC Account *</label>
            <select
              name="sfmcAccountId"
              value={formData.sfmcAccountId}
              onChange={handleChange}
              required
            >
              <option value="">Select an account</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Config Name *</label>
            <input
              type="text"
              name="configName"
              value={formData.configName}
              onChange={handleChange}
              required
              placeholder="Welcome Email Automation"
            />
          </div>

          <div className="form-group">
            <label>Object Type *</label>
            <select
              name="objectType"
              value={formData.objectType}
              onChange={handleChange}
              required
            >
              <optgroup label="Basic Monitoring">
                <option value="automation">Automation</option>
                <option value="trigger">Triggered Send</option>
                <option value="dataextension">Data Extension</option>
                <option value="journey">Journey</option>
              </optgroup>
              <optgroup label="Threshold Monitoring">
                <option value="dataextension_threshold">Data Extension Row Count</option>
                <option value="triggered_send_threshold">Triggered Send Queue</option>
                <option value="journey_threshold">Journey Injection Count</option>
                <option value="journey_injection">Journey Zero Injection Alert</option>
              </optgroup>
              <optgroup label="Advanced Monitoring">
                <option value="bounce_rate">Bounce Rate Monitor</option>
                <option value="automation_errors">Automation Errors</option>
              </optgroup>
            </select>
          </div>

          <div className="form-group">
            <label>Object ID *</label>
            <input
              type="text"
              name="objectId"
              value={formData.objectId}
              onChange={handleChange}
              required
              placeholder="SFMC Object ID or External Key"
            />
          </div>

          <div className="form-group">
            <label>Object Name *</label>
            <input
              type="text"
              name="objectName"
              value={formData.objectName}
              onChange={handleChange}
              required
              placeholder="Friendly name for this object"
            />
          </div>

          {/* Threshold fields for threshold-based monitoring types */}
          {['dataextension_threshold', 'triggered_send_threshold', 'journey_threshold', 'bounce_rate'].includes(formData.objectType) && (
            <div style={{ display: 'flex', gap: '15px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>
                  {formData.objectType === 'dataextension_threshold' ? 'Min Row Count' :
                   formData.objectType === 'triggered_send_threshold' ? 'Max Queue Depth' :
                   formData.objectType === 'bounce_rate' ? 'N/A' :
                   'Min Injections'}
                </label>
                <input
                  type="number"
                  name="thresholdMin"
                  value={formData.thresholdMin}
                  onChange={handleChange}
                  placeholder={formData.objectType === 'triggered_send_threshold' || formData.objectType === 'bounce_rate' ? 'N/A' : 'Optional'}
                  disabled={formData.objectType === 'triggered_send_threshold' || formData.objectType === 'bounce_rate'}
                />
                <small style={{ color: '#6b7280', fontSize: '11px' }}>
                  {formData.objectType === 'dataextension_threshold' ? 'Alert if below this count' :
                   formData.objectType === 'triggered_send_threshold' ? 'Not applicable' :
                   formData.objectType === 'bounce_rate' ? 'Not applicable' :
                   'Alert if below this count'}
                </small>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>
                  {formData.objectType === 'dataextension_threshold' ? 'Max Row Count' :
                   formData.objectType === 'triggered_send_threshold' ? 'Max Queue Depth' :
                   formData.objectType === 'bounce_rate' ? 'Max Bounce Rate %' :
                   'Max Injections'}
                </label>
                <input
                  type="number"
                  name="thresholdMax"
                  value={formData.thresholdMax}
                  onChange={handleChange}
                  placeholder={formData.objectType === 'triggered_send_threshold' ? '1000' : formData.objectType === 'bounce_rate' ? '5' : 'Optional'}
                />
                <small style={{ color: '#6b7280', fontSize: '11px' }}>
                  {formData.objectType === 'dataextension_threshold' ? 'Alert if above this count' :
                   formData.objectType === 'triggered_send_threshold' ? 'Alert if queue exceeds this (default: 1000)' :
                   formData.objectType === 'bounce_rate' ? 'Alert if bounce rate exceeds this % (default: 5%)' :
                   'Alert if above this count'}
                </small>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Check Interval (minutes) *</label>
            <select
              name="checkIntervalMinutes"
              value={formData.checkIntervalMinutes}
              onChange={handleChange}
              required
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={240}>4 hours</option>
              <option value={720}>12 hours</option>
              <option value={1440}>24 hours</option>
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                name="sendInstantAlerts"
                checked={formData.sendInstantAlerts}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Send instant alerts on failures
            </label>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                name="includeInDailyReport"
                checked={formData.includeInDailyReport}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Include in daily report
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingConfig ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Automation History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => { setShowHistoryModal(false); setHistoryData(null); setHistoryConfig(null); }}
        title={historyConfig ? `Run History: ${historyConfig.config_name}` : 'Automation Run History'}
      >
        {historyLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Loading automation history...
          </div>
        ) : historyData ? (
          <div>
            {/* Summary Stats */}
            {historyData.summary && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ padding: '12px', background: '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>{historyData.summary.totalRuns}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Runs</div>
                </div>
                <div style={{ padding: '12px', background: '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#065f46' }}>{historyData.summary.successfulRuns}</div>
                  <div style={{ fontSize: '12px', color: '#065f46' }}>Successful</div>
                </div>
                <div style={{ padding: '12px', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#991b1b' }}>{historyData.summary.errorRuns}</div>
                  <div style={{ fontSize: '12px', color: '#991b1b' }}>Errors</div>
                </div>
                <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1d4ed8' }}>{historyData.summary.avgDurationMinutes}m</div>
                  <div style={{ fontSize: '12px', color: '#1d4ed8' }}>Avg Duration</div>
                </div>
              </div>
            )}

            {/* Simple Bar Chart for Run Durations */}
            {historyData.runs && historyData.runs.length > 0 ? (
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>Run Duration Over Time (Last 7 Days)</h4>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '4px',
                  height: '150px',
                  padding: '10px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {historyData.runs.slice(-20).map((run, idx) => {
                    const maxDuration = Math.max(...historyData.runs.map(r => r.durationMinutes || 0), 1);
                    const height = run.durationMinutes ? (run.durationMinutes / maxDuration) * 120 : 5;
                    const isError = run.status === 'Error' || run.status === 'Failed';
                    return (
                      <div
                        key={idx}
                        style={{
                          flex: 1,
                          minWidth: '20px',
                          maxWidth: '40px',
                          height: `${Math.max(height, 5)}px`,
                          background: isError ? '#ef4444' : '#22c55e',
                          borderRadius: '3px 3px 0 0',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s'
                        }}
                        title={`${new Date(run.startTime).toLocaleString()}\nDuration: ${run.durationMinutes || 'N/A'} min\nStatus: ${run.status}`}
                        onMouseOver={(e) => e.target.style.opacity = '0.8'}
                        onMouseOut={(e) => e.target.style.opacity = '1'}
                      />
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: '#9ca3af' }}>
                  <span>{historyData.runs.length > 0 ? new Date(historyData.runs[0].startTime).toLocaleDateString() : ''}</span>
                  <span>{historyData.runs.length > 0 ? new Date(historyData.runs[historyData.runs.length - 1].startTime).toLocaleDateString() : ''}</span>
                </div>
                <div style={{ display: 'flex', gap: '15px', marginTop: '10px', fontSize: '12px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '12px', height: '12px', background: '#22c55e', borderRadius: '2px' }}></span>
                    Success
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px' }}></span>
                    Error
                  </span>
                </div>

                {/* Recent Runs Table */}
                <h4 style={{ margin: '20px 0 12px 0', fontSize: '14px', color: '#374151' }}>Recent Runs</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <table className="table" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th>Start Time</th>
                        <th>Duration</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.runs.slice().reverse().slice(0, 10).map((run, idx) => (
                        <tr key={idx}>
                          <td>{new Date(run.startTime).toLocaleString()}</td>
                          <td>{run.durationMinutes ? `${run.durationMinutes} min` : 'N/A'}</td>
                          <td>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              background: run.status === 'Complete' || run.status === 'Completed' ? '#d1fae5' : '#fee2e2',
                              color: run.status === 'Complete' || run.status === 'Completed' ? '#065f46' : '#991b1b'
                            }}>
                              {run.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No run history available for this automation.
              </div>
            )}

            <div className="modal-footer" style={{ marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowHistoryModal(false); setHistoryData(null); setHistoryConfig(null); }}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            No data available.
          </div>
        )}
      </Modal>
    </div>
  );
}

export default MonitoringConfigs;
